
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { api } from '../../services/api';
import { ProjectTemplate } from '../../api/types';

const ProjectsNew: React.FC = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [step, setStep] = useState(1);

    // Form State
    const [selectedTemplate, setSelectedTemplate] = useState<string>('');
    const [name, setName] = useState('');
    const [domain, setDomain] = useState('');
    const [platformType, setPlatformType] = useState('web');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        try {
            const data = await api.getTemplates();
            setTemplates(data);
        } catch (error) {
            console.error("Error loading templates", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!name || !selectedTemplate) return;
        setIsSubmitting(true);
        try {
            await api.createProject({
                name,
                template_id: selectedTemplate,
                domain,
                platform_type: platformType
            });
            navigate('/projects');
        } catch (e) {
            alert("Error creating project");
            console.error(e);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-5xl mx-auto">
            <button onClick={() => navigate('/projects')} className="flex items-center text-slate-500 hover:text-white mb-6">
                <ArrowLeft className="w-4 h-4 mr-2" /> Volver
            </button>

            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-2">Nuevo Proyecto</h1>
                <p className="text-slate-400">Configura tu nuevo workspace seleccionando una plantilla base.</p>
            </div>

            {/* Steps Indicator */}
            <div className="flex items-center justify-center bg-surface-dark border border-slate-800 p-4 rounded-xl mb-8">
                <div className={`flex items-center gap-2 ${step >= 1 ? 'text-primary' : 'text-slate-600'}`}>
                    <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center font-bold">1</div>
                    <span>Elegir Plantilla</span>
                </div>
                <div className="w-16 h-[2px] bg-slate-800 mx-4" />
                <div className={`flex items-center gap-2 ${step >= 2 ? 'text-primary' : 'text-slate-600'}`}>
                    <div className="w-8 h-8 rounded-full border-2 border-current flex items-center justify-center font-bold">2</div>
                    <span>Detalles</span>
                </div>
            </div>

            {loading ? (
                <div className="text-center text-slate-500">Cargando plantillas...</div>
            ) : (
                <div>
                    {/* STEP 1: SELECT TEMPLATE */}
                    {step === 1 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                            {templates.map(tmpl => (
                                <div
                                    key={tmpl.id}
                                    onClick={() => setSelectedTemplate(tmpl.id)}
                                    className={`p-6 rounded-xl border cursor-pointer transition-all ${selectedTemplate === tmpl.id
                                        ? 'bg-primary/10 border-primary ring-1 ring-primary'
                                        : 'bg-surface-dark border-slate-700 hover:border-slate-500'
                                        }`}
                                >
                                    <div className="flex justify-between items-start mb-3">
                                        <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center text-2xl">
                                            {tmpl.industry === 'banking' ? '🏦' : tmpl.industry === 'retail' ? '🛒' : '📱'}
                                        </div>
                                        {selectedTemplate === tmpl.id && <CheckCircle2 className="w-6 h-6 text-primary" />}
                                    </div>
                                    <h3 className="text-white font-semibold text-lg">{tmpl.name}</h3>
                                    <p className="text-slate-400 text-sm mt-2">{tmpl.description}</p>
                                    <div className="mt-4 flex gap-2">
                                        <span className="text-[10px] bg-slate-800 text-slate-300 px-2 py-1 rounded border border-slate-700 uppercase">{tmpl.industry}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* STEP 2: DETAILS */}
                    {step === 2 && (
                        <div className="max-w-md mx-auto mt-8 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Nombre del Proyecto</label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full bg-slate-800 border-slate-700 rounded-lg px-4 py-2 text-white focus:ring-primary focus:border-primary"
                                    placeholder="Ej: Banco Core V2"
                                    autoFocus
                                />
                            </div>
                            
                            {/* NEW: Platform Type */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Tipo de Plataforma</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setPlatformType('web')}
                                        className={`p-3 rounded-lg border text-sm text-center font-medium transition-colors ${platformType === 'web' ? 'bg-primary/20 border-primary text-white' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-300'}`}
                                    >
                                        🌐 Aplicación Web
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPlatformType('mobile')}
                                        className={`p-3 rounded-lg border text-sm text-center font-medium transition-colors ${platformType === 'mobile' ? 'bg-primary/20 border-primary text-white' : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-300'}`}
                                    >
                                        📱 App Móvil (Nativa)
                                    </button>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">Dominio / Área (Opcional)</label>
                                <select
                                    value={domain}
                                    onChange={(e) => setDomain(e.target.value)}
                                    className="w-full bg-slate-800 border-slate-700 rounded-lg px-4 py-2 text-white"
                                >
                                    <option value="">Seleccionar...</option>
                                    <option value="banking">Banking</option>
                                    <option value="retail">Retail</option>
                                    <option value="telco">Telco</option>
                                    <option value="insurance">Insurance</option>
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-4 mt-8 pt-8 border-t border-slate-800">
                        {step === 2 && (
                            <button
                                onClick={() => setStep(1)}
                                className="px-6 py-2 text-slate-400 hover:text-white"
                            >
                                Atrás
                            </button>
                        )}

                        {step === 1 ? (
                            <button
                                onClick={() => setStep(2)}
                                disabled={!selectedTemplate}
                                className="px-6 py-2 bg-primary text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600"
                            >
                                Continuar
                            </button>
                        ) : (
                            <button
                                onClick={handleSubmit}
                                disabled={!name || isSubmitting}
                                className="px-6 py-2 bg-primary text-white rounded-lg disabled:opacity-50 hover:bg-blue-600 flex items-center gap-2"
                            >
                                {isSubmitting ? 'Creando...' : 'Crear Proyecto'}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectsNew;
